import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { DateOnly } from '@sevenrooms/timepiece'
import { useMaxWidthBreakpoint } from '@sevenrooms/ui-kit/hooks'
import { Window } from '@sevenrooms/ui-kit/layout'
import type { ThemeSpacing } from '@sevenrooms/ui-kit/theme'
import { RequestOrPriorityAlertButton } from '../../../components/RequestOrPriorityAlertButton'
import { type AvailabilityTimeWithUpSellCost, type ExperienceWithAvailability, useVenue } from '../../../hooks'
import { useModals, useReservationFormState } from '../../../store'
import { AvailabilityDatesModal } from '../AvailabilityDatesModal'
import { ExperienceCard, type ExperienceCardProps, type ExperienceVenueInfo } from '../ExperienceCard'
import { ExperienceNoAvailability } from '../ExperienceNoAvailability'
import { CreatePriorityAlert } from '../PriorityAlert/CreatePriorityAlert'
import { CreatePriorityAlertSuccessful } from '../PriorityAlert/CreatePriorityAlertSuccessful'
import { CreateRequest } from '../Request/CreateRequest'
import { ExperiencesContainer } from './ExperiencesContainer'
import { ExperiencesSkeleton } from './ExperiencesSkeleton'
import type { OtherVenueInfo } from '@/domain'

export interface ExperiencesProps {
  isPrivateEvents?: boolean
  experiences?: ExperienceWithAvailability[]
  otherVenue?: OtherVenueInfo
  onTimeSlotClick?: (slot: AvailabilityTimeWithUpSellCost, experience: ExperienceWithAvailability) => void
  onMoreDetailsClick?: (experience: ExperienceWithAvailability) => void
  onShowMoreClick?: () => void
  onClearTimeFilter?: () => void
  desktopVariant?: ExperienceCardProps['variant']
  descriptionType?: ExperienceCardProps['descriptionType']
  direction?: ExperienceCardProps['direction']
  timeSlotVariant?: ExperienceCardProps['timeSlotVariant']
  partySize?: number
  onPartySizeChange?: (value: number) => void
  startDate?: string
  onStartDateChange?: (value: Date) => void
  startTime?: string
  haloTimeIntervalMinutes?: number
  onRequestModalBackClick?: () => void
  showViewAllAvailableDatesButton?: boolean
  showNextAvailableDay?: boolean
  hideRequestButton?: boolean
  useHorizontalScroll?: boolean
  scrollRightMargin?: ThemeSpacing
  label?: ReactNode
  isLoading?: boolean
  isError?: boolean
}
export function Experiences({
  desktopVariant = 'full',
  descriptionType,
  direction,
  timeSlotVariant,
  isPrivateEvents,
  onTimeSlotClick,
  onMoreDetailsClick,
  onClearTimeFilter,
  onShowMoreClick,
  experiences,
  partySize,
  onPartySizeChange,
  startDate,
  onStartDateChange,
  startTime,
  haloTimeIntervalMinutes,
  isLoading,
  isError,
  onRequestModalBackClick,
  otherVenue,
  showViewAllAvailableDatesButton,
  showNextAvailableDay,
  hideRequestButton,
  label,
  useHorizontalScroll,
  scrollRightMargin,
}: ExperiencesProps) {
  const isSmallBreakpoint = useMaxWidthBreakpoint('s')
  const isExtraSmallBreakpoint = useMaxWidthBreakpoint('xs')
  const currentVenue = useVenue()
  const venueInfo: ExperienceVenueInfo = useMemo(() => {
    const { urlKey, currencyCode, venueToday, availabilityCacheDays, startOfDayTime } = otherVenue || currentVenue
    return { id: otherVenue?.venueId || currentVenue.id, urlKey, currencyCode, venueToday, availabilityCacheDays, startOfDayTime }
  }, [currentVenue, otherVenue])

  const { showModal, hideModal, activeModal, previousActiveModal, showErrorModal } = useModals()
  const {
    formState: { selectedExperience, clientId },
    updateFormState,
  } = useReservationFormState()
  const [requestDate, setRequestDate] = useState(startDate)
  const [requestPartySize, setRequestPartySize] = useState(partySize)
  const columnsCount = isSmallBreakpoint || desktopVariant === 'full' ? 1 : 2

  const timeSlotsDisplayCount = useMemo(() => {
    if (isExtraSmallBreakpoint) {
      return 2
    } else if (isSmallBreakpoint) {
      return 3
    } else if (columnsCount > 1) {
      return 2
    }
    return 4
  }, [columnsCount, isExtraSmallBreakpoint, isSmallBreakpoint])

  const onRequestBtnClick = useCallback(
    (experience: ExperienceWithAvailability, requestDate: string, requestPartySize: number) => {
      updateFormState({ selectedExperience: experience, selectedOtherVenue: otherVenue })
      setRequestDate(requestDate)
      setRequestPartySize(requestPartySize)
      showModal('createRequest')
    },
    [otherVenue, showModal, updateFormState]
  )
  const onPriorityAlertBtnClick = useCallback(
    (experience: ExperienceWithAvailability, requestDate: string, requestPartySize: number) => {
      updateFormState({ selectedExperience: experience })
      setRequestDate(requestDate)
      setRequestPartySize(requestPartySize)
      showModal('createPriorityAlert')
    },
    [showModal, updateFormState]
  )

  const onDateClick = useCallback(
    (dateIso: string) => {
      const value = DateOnly.fromSafe(dateIso)?.toJsDate()
      if (value) {
        onStartDateChange?.(value)
      }
    },
    [onStartDateChange]
  )

  const getRequestOrPriorityAlertAvailability = useCallback(
    (experience: ExperienceWithAvailability) => {
      if (!hideRequestButton && experience.requestTimeRange?.length && partySize && startDate) {
        return (
          <RequestOrPriorityAlertButton
            isPrivateEvent={isPrivateEvents}
            buttonVariant={experience.reservationTimesInHalo?.length === 0 ? 'primary' : 'secondary'}
            onRequestButtonClick={() => onRequestBtnClick(experience, startDate, partySize)}
            onPriorityAlertButtonClick={() => onPriorityAlertBtnClick(experience, startDate, partySize)}
            partySize={partySize}
          />
        )
      }
      return null
    },
    [hideRequestButton, isPrivateEvents, onPriorityAlertBtnClick, onRequestBtnClick, partySize, startDate]
  )
  const getNoAvailabilityMessage = useCallback(
    (experience: ExperienceWithAvailability) => (
      <ExperienceNoAvailability
        experience={experience}
        startDate={startDate}
        partySize={partySize}
        isPrivateEvent={!!isPrivateEvents}
        onClearTimeFilter={onClearTimeFilter}
        onPriorityAlertBtnClick={onPriorityAlertBtnClick}
        onRequestBtnClick={onRequestBtnClick}
      />
    ),
    [isPrivateEvents, onClearTimeFilter, onPriorityAlertBtnClick, onRequestBtnClick, partySize, startDate]
  )

  const onRequestModalClose = useCallback(() => {
    updateFormState({ selectedExperience: undefined })
    hideModal()
  }, [hideModal, updateFormState])

  const onShowAllAvailableDatesClick = useCallback(
    (experience: ExperienceWithAvailability) => {
      updateFormState({ selectedExperience: experience })
      showModal('viewAllExperienceDates')
    },
    [showModal, updateFormState]
  )

  useEffect(() => {
    if (isError) {
      showErrorModal()
    }
  }, [isError, showErrorModal])

  if (isLoading) {
    return <ExperiencesSkeleton desktopVariant={desktopVariant} />
  }

  if (!experiences) {
    return null
  }

  return (
    <>
      <ExperiencesContainer
        label={label}
        columnsCount={columnsCount}
        useHorizontalScroll={useHorizontalScroll}
        scrollRightMargin={scrollRightMargin}
      >
        {experiences.map(experience => (
          <ExperienceCard
            key={experience.id}
            experience={experience}
            direction={direction ?? (columnsCount === 1 ? 'column' : 'row')}
            variant={isSmallBreakpoint ? 'short' : desktopVariant}
            timeSlotVariant={timeSlotVariant}
            descriptionType={descriptionType}
            partySize={partySize}
            startDate={startDate}
            startTime={startTime}
            haloTimeIntervalMinutes={haloTimeIntervalMinutes}
            clientId={clientId}
            venueInfo={venueInfo}
            timeSlotsDisplayCount={timeSlotsDisplayCount}
            onTimeSlotClick={timeSlot => onTimeSlotClick?.(timeSlot, experience)}
            onDateClick={onDateClick}
            onShowMoreClick={onShowMoreClick}
            onMoreDetailsClick={() => onMoreDetailsClick?.(experience)}
            onRequestBtnClick={(requestDate, requestPartySize) => onRequestBtnClick(experience, requestDate, requestPartySize)}
            onPriorityAlertBtnClick={(requestDate, requestPartySize) => onPriorityAlertBtnClick(experience, requestDate, requestPartySize)}
            onShowAllAvailableDatesClick={onShowAllAvailableDatesClick}
            requestOrPriorityAlertAvailability={getRequestOrPriorityAlertAvailability(experience)}
            noAvailabilityMessage={getNoAvailabilityMessage(experience)}
            showViewAllAvailableDatesButton={showViewAllAvailableDatesButton}
            showNextAvailableDay={showNextAvailableDay}
          />
        ))}
      </ExperiencesContainer>
      {activeModal === 'createRequest' && selectedExperience && requestPartySize !== undefined && requestDate && (
        <Window active>
          <CreateRequest
            partySize={requestPartySize}
            startDate={requestDate}
            validRequestTimes={selectedExperience.requestTimeRange || []}
            onClose={onRequestModalClose}
            onBack={previousActiveModal === 'viewAllExperienceDates' ? onRequestModalBackClick : undefined}
          />
        </Window>
      )}
      {activeModal === 'createRequestSuccess' && requestPartySize !== undefined && requestDate && (
        <Window active>
          <CreatePriorityAlertSuccessful partySize={requestPartySize} startDate={requestDate} isPriorityAlert={false} onClose={hideModal} />
        </Window>
      )}
      {activeModal === 'createPriorityAlert' && requestPartySize !== undefined && selectedExperience && requestDate && (
        <Window active>
          <CreatePriorityAlert
            partySize={requestPartySize}
            startDate={requestDate}
            validRequestTimes={selectedExperience.requestTimeRange || []}
            onClose={hideModal}
            onBack={previousActiveModal === 'viewAllExperienceDates' ? onRequestModalBackClick : undefined}
          />
        </Window>
      )}
      {activeModal === 'createPriorityAlertSuccess' && requestPartySize !== undefined && requestDate && (
        <Window active>
          <CreatePriorityAlertSuccessful isPriorityAlert partySize={requestPartySize} startDate={requestDate} onClose={hideModal} />
        </Window>
      )}
      {selectedExperience && startDate && partySize && activeModal === 'viewAllExperienceDates' && (
        <Window active>
          <AvailabilityDatesModal
            experience={selectedExperience}
            venueInfo={venueInfo}
            onClose={hideModal}
            onPartySizeChange={onPartySizeChange}
            onStartDateChange={onStartDateChange}
            partySize={partySize}
            startDate={startDate}
            clientId={clientId}
            onTimeSlotClick={timeSlot => onTimeSlotClick?.(timeSlot, selectedExperience)}
            onRequestBtnClick={(requestDate, requestPartySize) => onRequestBtnClick(selectedExperience, requestDate, requestPartySize)}
            onPriorityAlertBtnClick={(requestDate, requestPartySize) =>
              onPriorityAlertBtnClick(selectedExperience, requestDate, requestPartySize)
            }
          />
        </Window>
      )}
    </>
  )
}
